/**
 * Live event snapshot + category-pair synergy (keep in sync with
 * apps/api/src/liveEventSynergy.ts).
 */
import { isTriviaCategoryKey } from "@/lib/trivia/catalog";

export type SnapshotEventEntry = {
  key: string;
  featuredCategories: string[];
  bonusSynergy: number;
};

export type LiveEventSnapshotV1 = {
  version: 1;
  events: SnapshotEventEntry[];
};

export function normalizeRulesToSnapshotEntry(
  eventKey: string,
  rules: Record<string, unknown>,
): SnapshotEventEntry {
  const keys: string[] = [];
  const fc = rules.featuredCategories;
  if (Array.isArray(fc)) {
    for (const x of fc) {
      if (typeof x === "string" && isTriviaCategoryKey(x)) keys.push(x);
    }
  }
  const tb = rules.tagBonus;
  if (keys.length < 2 && Array.isArray(tb)) {
    for (const x of tb) {
      if (typeof x === "string" && isTriviaCategoryKey(x)) keys.push(x);
    }
  }
  const uniq = [...new Set(keys)];
  const bonusSynergy = Number(rules.bonusSynergy ?? 0);
  return {
    key: eventKey,
    featuredCategories: uniq,
    bonusSynergy: Number.isFinite(bonusSynergy) ? bonusSynergy : 0,
  };
}

export function buildLiveEventSnapshotPayload(
  activeEvents: Array<{ key: string; rules: unknown }>,
): LiveEventSnapshotV1 {
  const events: SnapshotEventEntry[] = [];
  for (const ev of activeEvents) {
    const rules =
      ev.rules && typeof ev.rules === "object"
        ? (ev.rules as Record<string, unknown>)
        : {};
    events.push(normalizeRulesToSnapshotEntry(ev.key, rules));
  }
  return { version: 1, events };
}

export function synergyBonusPointsForAnswer(
  snapshot: unknown,
  wedgesBefore: ReadonlySet<string>,
  wedgesAfter: ReadonlySet<string>,
): number {
  if (!snapshot || typeof snapshot !== "object") return 0;
  const s = snapshot as Record<string, unknown>;
  if (s.version === 1 && Array.isArray(s.events)) {
    return synergyFromEventArray(s.events, wedgesBefore, wedgesAfter);
  }
  if (Array.isArray(s.events)) {
    return synergyFromEventArray(s.events, wedgesBefore, wedgesAfter);
  }
  return 0;
}

function synergyFromEventArray(
  rawEvents: unknown[],
  wedgesBefore: ReadonlySet<string>,
  wedgesAfter: ReadonlySet<string>,
): number {
  let total = 0;
  for (const ev of rawEvents) {
    if (!ev || typeof ev !== "object") continue;
    const e = ev as Record<string, unknown>;
    const fc = e.featuredCategories;
    const bonus = Number(e.bonusSynergy ?? 0);
    if (!Number.isFinite(bonus) || bonus <= 0) continue;
    if (!Array.isArray(fc) || fc.length < 2) continue;
    const pairKeys = [
      ...new Set(
        fc.filter(
          (x): x is string =>
            typeof x === "string" && isTriviaCategoryKey(x),
        ),
      ),
    ];
    if (pairKeys.length < 2) continue;
    const c1 = pairKeys[0]!;
    const c2 = pairKeys[1]!;
    const hadPairBefore = wedgesBefore.has(c1) && wedgesBefore.has(c2);
    const hasPairAfter = wedgesAfter.has(c1) && wedgesAfter.has(c2);
    if (hasPairAfter && !hadPairBefore) total += bonus;
  }
  return total;
}
